import type { CapacityPlantState, CapacitySeed, CapacityStrategySeed } from '@/versions/v1/schema/capacity';
import { yearOneScheduledHours } from './bunker-hill-planning-seed';
import { READINGS } from './bunker-hill-readings';
import { INITIAL_SESSION_STATE } from '@/versions/v1/schema/scenario';
import { valueAt } from './series.derive';

const recoveryDesign = (id: string): number => {
  const design = READINGS.find(row => row.id === id)?.design;
  if (design === undefined) throw new Error(`Missing capacity recovery design: ${id}`);
  return design;
};

// K11c / D23: each metal uses its own measured reading and design.
// The cap is max(design, measured); Ag's 84.2% design exceeds its 83.6% reading.
export const CAPACITY_DESIGN_RECOVERIES = {
  znPct: recoveryDesign('rd-kpi-zn-rec'),
  pbPct: recoveryDesign('rd-kpi-pb-rec'),
  agPct: recoveryDesign('rd-kpi-ag-rec'),
};

// [syn] Default basis for the Capacity & strategy mock. Each value below is
// synthetic or the explicitly accepted A1 reference input; M1 §§5.2, 5.4, 6.1
// and M12 §§3.1–3.2 define the model, with A1 resolving the contradictory
// typed figures in Paul's separate HTML and documents.
export const CAPACITY_SEED: CapacitySeed = {
  grade: {
    zn: 0.0544, // [syn] D14 DEFAULT_CURSOR fallback mirrors rd-head-zn; replaces typed 6.8% A1 basis.
    pb: 0.024, // [syn] D14 DEFAULT_CURSOR fallback mirrors rd-head-pb; replaces typed 4.2% A1 basis.
    agOzPerShortTon: 0.442, // [syn] D14 DEFAULT_CURSOR fallback mirrors rd-head-ag; replaces typed 0.86 oz/st A1 basis.
    as: 0.0002, // [syn] M1 §6.1 / A1 — 0.020% arsenic feed grade.
  },
  sourceTags: {
    currentFeed: 'WIT-021', // [doc] DataRequest tag table — ball mill feed belt scale.
    grindingCyclone: 'PT-173', // [doc] DataRequest tag table — cyclone package pressure transmitter.
  },
  currentThroughputStph: 57, // [syn] M1 §5.2 / A1 — reference operating point.
  targetThroughputStph: 60, // [syn] M5 §5 / A1 — current target.
  recoveryFloorStph: 65, // [syn] M1 §5.3 / A1 — throughput point where the recovery floor binds.
  recoveryFloorZnPct: 80.0, // [syn] M1 §5.2 / D13 / D14 — DEFAULT_CURSOR floor is 2.3 ppt below measured Zn recovery; re-derived to retain non-negative Step 1 at the feed-scaled grinding ceiling.
  overloadRecoveryLossPerStphPct: 2.75, // [syn] M1 §5.2 — baseline 65–67 stph high-rate Zn curve slope.
  gradeOverloadSaturationLossPct: 16, // [syn] M1 §5.2 — asymptotic added grade-overload loss for the affected circuit.
  gradeOverloadHalfSaturationRatio: 0.08, // [syn] M1 §5.2 — excess-load ratio where the added loss reaches half its maximum.
  gradeOverloadLinearTailStartRatio: 0.4, // [syn] M1 §5.2 — overload ratio above which loss keeps rising beyond saturation.
  gradeOverloadLinearTailPerRatioPct: 40, // [syn] M1 §5.2 / D15 — retained counterfactual overload tail; D18 caps installed feed before pricing. The old 55.1% at 11% Zn is an overloaded counterfactual, not current recovery. D22 phases grinding loss in continuously; the stricter US$1,000/day floor remains infeasible at the ceiling crossings.
  zincRecoveryAtCurrentPct: 82.3, // [syn] M1 §5.2 / D13 — DEFAULT_CURSOR fallback mirrors rd-kpi-zn-rec.
  zincRecoveryAtBeyondLimitPct: 74.5, // [syn] M1 §5.2 / D13 / D14 — 5.5 ppt below the floor after 2 reference stph at 2.75 ppt/stph.
  zincBeyondLimitStph: 67, // [syn] M1 §5.2 / A1 — reference Zn flotation ceiling.
  leadRecoveryAtCurrentPct: 85.1, // [syn] M1 §5.2 / D13 — DEFAULT_CURSOR fallback mirrors rd-kpi-pb-rec; Ag shares its curve shape only (K11c).
  leadRecoveryLossPerStphPct: 0.1, // [syn] M1 §5.3 / A1 — recovery slope above the current point.
  leadRecoveryFloorPct: 68, // [syn] M1 §5.3 / A1 — lower plotting bound for lead recovery.
  circuits: {
    crushingUtilisationAtReference: 0.72, // [syn] M1 §5.2 — crushing calibration.
    grindingCeilingStph: 63, // [syn] M1 §5.2 / A1 — money-anchored grinding ceiling.
    leadFlotationUtilisationAtReference: 0.78, // [syn] M1 §5.2 — Pb/Ag calibration.
    zincFlotationCeilingStph: 67, // [syn] M1 §5.2 / A1 — money-anchored Zn ceiling.
    dewateringUtilisationAtReference: 0.65, // [syn] M1 §5.2 — dewatering calibration.
  },
  operatingCostPerDstA: 89.62, // [syn] M12 §3.1, M1 §5.4 / D15 — retained current payable basis and original least-cent opex after standard Zn payable removed the Ag breakeven from the allowed slider; invariants hold across the 0.001% Zn grid in the US$45–100/dst band (discrepancy row 118).
  zincFlotationExpansionCapexA: 3500000, // [syn] M13 §4.1 — capital example basis, recalculated payback.
  daysPerYear: yearOneScheduledHours() / 24, // [syn] D12 / K8 — Year 1 operating days derived from PLANNING_MONTH_PROFILE.
};

// [syn] Seed-derived default for pure callers at DEFAULT_CURSOR. The live
// Capacity surface builds this same shape from readingsAt(sessionState).
export const DEFAULT_CAPACITY_PLANT_STATE: CapacityPlantState = {
  designRecoveries: CAPACITY_DESIGN_RECOVERIES,
  feedStph: 1720 / 24, // [syn] D14 — rd-kpi-ore-td, converted from 1,720 st/d.
  grade: {
    zn: 5.44 / 100, // [syn] D14 — rd-head-zn at DEFAULT_CURSOR.
    pb: 2.4 / 100, // [syn] D14 — rd-head-pb at DEFAULT_CURSOR.
    agOzPerShortTon: 0.442, // [syn] D14 — rd-head-ag at DEFAULT_CURSOR.
    as: CAPACITY_SEED.grade.as, // [syn] D14 does not replace the seeded As basis.
  },
  recoveries: {
    znPct: 82.3, // [syn] D13 — rd-kpi-zn-rec at DEFAULT_CURSOR.
    pbPct: 85.1, // [syn] D13 — rd-kpi-pb-rec at DEFAULT_CURSOR.
    agPct: valueAt('rd-kpi-ag-rec', INITIAL_SESSION_STATE)!, // K11c: same cursor accessor as readingsAt and NsrEngine; no second Ag constant.
  },
  concentrateGrades: {
    leadPct: 67.1, // [syn] D14 — rd-kpi-pb-grade at DEFAULT_CURSOR.
    zincPct: 58.4, // [syn] D14 — rd-kpi-zn-grade at DEFAULT_CURSOR.
  },
  zincInLeadConcentratePct: 1.2, // [syn] D14 — rd-pb-con-zn (SA03 final Pb concentrate) at DEFAULT_CURSOR.
  penaltyAssays: {
    leadAsPct: 0.32, // [syn] D14 — rd-spec-pb-as at DEFAULT_CURSOR.
    leadHgPpm: 4, // [syn] D14 — rd-spec-pb-hg at DEFAULT_CURSOR.
    zincAsPct: 0.06, // [syn] D14 — rd-spec-zn-as at DEFAULT_CURSOR.
    zincPbPct: 2.6, // [syn] D14 — rd-spec-zn-pb at DEFAULT_CURSOR.
  },
};

// --- K1 strategy tab (A8 / A7) --------------------------------------------
// [syn] Operating setpoints follow M12 §§4.1–4.3. These recovery uplifts are
// added to CapacityEngine's curve at each strategy feed. Balanced beats both
// the current point and tab 1's 60 stph Optimised case; Strategy C trades Zn
// recovery for Pb/Ag recovery and crosses Balanced inside the Ag slider range.
// Paul's separately typed 58 stph / 71.5% Zn recovery and ~$12/oz crossover
// are recorded in discrepancy rows 64–66 and never rendered.
export const STRATEGY_APPROVAL_REVIEW_ID = 'FMR-0022';

export const STRATEGY_PRICE_LIMITS = {
  zincMin: 1_500,
  zincMax: 4_000,
  zincStep: 10,
  leadMin: 1_000,
  leadMax: 3_000,
  leadStep: 10,
  silverMin: 15,
  silverMax: 130,
  silverStep: 0.5,
} as const;

const SHARED_LEVERS = {
  feed: {
    id: 'feed-rate', group: 'shared', label: 'Feed rate', currentValue: '57 stph',
    driver: 'Current WIT-021 feed against the 60 stph operating target',
  },
  p80: {
    id: 'grind-p80', group: 'shared', label: 'Grind P80 target', currentValue: '91 µm',
    driver: 'Finer grind supports liberation at the selected feed rate',
  },
} as const;

const LEAD_LEVERS = {
  conditionerPh: {
    id: 'lead-conditioner-ph', group: 'lead-silver', label: 'Pb conditioner pH (AT-128)',
    currentValue: '8.9', driver: 'Pb/Ag activation at the selected pH',
  },
  rougherAir: {
    id: 'lead-rougher-air', group: 'lead-silver', label: 'Pb rougher air (FT-131)',
    currentValue: '4.2 SCFM', driver: 'Bubble coverage at the Pb rougher',
  },
  rougherLevel: {
    id: 'lead-rougher-level', group: 'lead-silver', label: 'Pb rougher level (LT-140)',
    currentValue: '38%', driver: 'Froth depth at the Pb rougher',
  },
} as const;

const ZINC_LEVERS = {
  rougherPh: {
    id: 'zinc-rougher-ph', group: 'zinc', label: 'Zn rougher pH (AT-150)',
    currentValue: '10.4', driver: 'Lime delivery restores the Zn activation target',
  },
  copperSulfate: {
    id: 'copper-sulfate-dose', group: 'zinc', label: 'CuSO₄ dose rate',
    currentValue: '462 g/t', driver: 'Post-titration batch supports Zn activation',
  },
  rougherLevel: {
    id: 'zinc-rougher-level', group: 'zinc', label: 'Zn rougher level (LT-160)',
    currentValue: '55%', driver: 'Froth depth balances grade and recovery',
  },
} as const;

export const STRATEGY_LEVERS: readonly CapacityStrategySeed[] = [
  {
    id: 'balanced',
    name: 'Balanced',
    feedStph: 60,
    recoveryUplift: { znPct: 3.2, pbPct: 1.2, agPct: 1.2 }, // [syn] M12 §§4.1–4.2; gains over engine curve at 60 stph.
    quality: {
      zincInLeadConcentratePct: 3.5,
      leadConcentrateZnThresholdPct: 8,
      zincConcentrateGradePct: 57,
      zincConcentrateGradeMinimumPct: 55,
    },
    levers: [
      { ...SHARED_LEVERS.feed, recommendedValue: '60 stph' },
      { ...SHARED_LEVERS.p80, recommendedValue: '86 µm', recoveryDeltaShare: { znPct: 0.2, pbPct: 0.15, agPct: 0.15 } },
      { ...LEAD_LEVERS.conditionerPh, recommendedValue: '9.4', recoveryDeltaShare: { pbPct: 0.3, agPct: 0.3 } },
      { ...LEAD_LEVERS.rougherAir, recommendedValue: '5.4 SCFM', recoveryDeltaShare: { pbPct: 0.25, agPct: 0.25 } },
      { ...LEAD_LEVERS.rougherLevel, recommendedValue: '44%', recoveryDeltaShare: { pbPct: 0.3, agPct: 0.3 } },
      { ...ZINC_LEVERS.rougherPh, recommendedValue: '11.0', recoveryDeltaShare: { znPct: 0.4 } },
      { ...ZINC_LEVERS.copperSulfate, recommendedValue: '490 g/t', recoveryDeltaShare: { znPct: 0.3 } },
      { ...ZINC_LEVERS.rougherLevel, recommendedValue: '62%', recoveryDeltaShare: { znPct: 0.1 } },
    ],
  },
  {
    id: 'zinc-focus',
    name: 'Zn recovery focus',
    feedStph: 58,
    recoveryUplift: { znPct: 2.5, pbPct: 1, agPct: 1 }, // [syn] M12 §4.2; gains over engine curve at 58 stph.
    quality: {
      zincInLeadConcentratePct: 3.5,
      leadConcentrateZnThresholdPct: 8,
      zincConcentrateGradePct: 57.5,
      zincConcentrateGradeMinimumPct: 55,
    },
    levers: [
      { ...SHARED_LEVERS.feed, recommendedValue: '58 stph' },
      { ...SHARED_LEVERS.p80, recommendedValue: '84 µm', recoveryDeltaShare: { znPct: 0.2, pbPct: 0.15, agPct: 0.15 } },
      { ...LEAD_LEVERS.conditionerPh, recommendedValue: '9.2', recoveryDeltaShare: { pbPct: 0.3, agPct: 0.3 } },
      { ...LEAD_LEVERS.rougherAir, recommendedValue: '5.0 SCFM', recoveryDeltaShare: { pbPct: 0.25, agPct: 0.25 } },
      { ...LEAD_LEVERS.rougherLevel, recommendedValue: '42%', recoveryDeltaShare: { pbPct: 0.3, agPct: 0.3 } },
      { ...ZINC_LEVERS.rougherPh, recommendedValue: '11.2', recoveryDeltaShare: { znPct: 0.4 } },
      { ...ZINC_LEVERS.copperSulfate, recommendedValue: '500 g/t', recoveryDeltaShare: { znPct: 0.3 } },
      { ...ZINC_LEVERS.rougherLevel, recommendedValue: '65%', recoveryDeltaShare: { znPct: 0.1 } },
    ],
  },
  {
    id: 'lead-silver-max',
    name: 'Pb/Ag maximum recovery',
    feedStph: 60,
    recoveryUplift: { znPct: 1.1, pbPct: 3.7, agPct: 3.7 }, // [syn] M12 §4.3; Zn yield held below Balanced as Pb/Ag recovery rises.
    quality: {
      zincInLeadConcentratePct: 5.8,
      leadConcentrateZnThresholdPct: 8,
      zincConcentrateGradePct: 56.2,
      zincConcentrateGradeMinimumPct: 55,
    },
    levers: [
      { ...SHARED_LEVERS.feed, recommendedValue: '60 stph' },
      { ...SHARED_LEVERS.p80, recommendedValue: '84 µm', recoveryDeltaShare: { znPct: 0.2, pbPct: 0.1, agPct: 0.1 } },
      { ...LEAD_LEVERS.conditionerPh, recommendedValue: '9.6', recoveryDeltaShare: { pbPct: 0.25, agPct: 0.25 } },
      { ...LEAD_LEVERS.rougherAir, recommendedValue: '6.2 SCFM', recoveryDeltaShare: { pbPct: 0.25, agPct: 0.25 } },
      { ...LEAD_LEVERS.rougherLevel, recommendedValue: '52%', recoveryDeltaShare: { pbPct: 0.2, agPct: 0.2 } },
      {
        id: 'zinc-sulfate-dose', group: 'lead-silver', label: 'ZnSO₄ dose (Pb circuit)',
        currentValue: '500 g/t', recommendedValue: '350 g/t',
        driver: 'Less Zn depression increases Pb/Ag collection and Zn in Pb concentrate',
        recoveryDeltaShare: { pbPct: 0.2, agPct: 0.2 },
      },
      { ...ZINC_LEVERS.rougherPh, recommendedValue: '11.0', recoveryDeltaShare: { znPct: 0.4 } },
      { ...ZINC_LEVERS.copperSulfate, recommendedValue: '490 g/t', recoveryDeltaShare: { znPct: 0.3 } },
      { ...ZINC_LEVERS.rougherLevel, recommendedValue: '58%', recoveryDeltaShare: { znPct: 0.1 } },
    ],
  },
];
